<template>
  <div class="bottom-box" :class="wrapClass">
    <div class="left-wrap">
      <woot-button
        v-tooltip.top-end="
          '' /*$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON') d99d-because of single conv view */
        "
        :title="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        icon="emoji"
        emoji="😊"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="toggleEmojiPicker"
      />
      <file-upload
        v-if="showAttachButton"
        ref="upload"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="fileKindsAcceptedToUpload || allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
        @input-filter="inputFilter"
      >
        <woot-button
          v-if="showAttachButton"
          class-names="button--upload"
          :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="attach"
          emoji="📎"
          color-scheme="secondary"
          variant="smooth"
          size="small"
        />
      </file-upload>
      <woot-button
        v-if="showAudioRecorderButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :icon="!isRecordingAudio ? 'microphone' : 'microphone-off'"
        emoji="🎤"
        :color-scheme="!isRecordingAudio ? 'secondary' : 'alert'"
        variant="smooth"
        size="small"
        @click="toggleAudioRecorder"
      />
      <woot-button
        v-if="showDocalButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_DOCAL')"
        :icon="'document'"
        :color-scheme="'secondary'"
        variant="smooth"
        size="small"
        @click="toggleDocal"
      />
      <docal-documents-modal
        :show="showDocalModal"
        :conversation-id="conversationId"
        @signing-link="onDocalSigningLink"
        @cancel="showDocalModal = false"
      />
      <woot-button
        v-if="showFormsButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMS')"
        :icon="'forms'"
        :color-scheme="'secondary'"
        variant="smooth"
        size="small"
        @click="toggleForms"
      />
      <forms-list-modal
        :show="showFormsModal"
        :conversation-id="conversationId"
        @form-link="onFormLink"
        @cancel="showFormsModal = false"
      />
      <woot-button
        v-if="showEditorToggle"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        icon="quote"
        emoji="🖊️"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="$emit('toggle-editor')"
      />
      <woot-button
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        emoji="🎤"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="toggleAudioRecorderPlayPause"
      >
        <span>{{ recordingAudioDurationText }}</span>
      </woot-button>
      <bafi-file-attacher
        v-if="showAttachButton"
        style="margin-inline-start: 1rem"
        :on-file-download="onFileUpload"
      />
      <woot-button
        v-if="hasWhatsappTemplates"
        v-tooltip.top-end="'Whatsapp Templates'"
        icon="whatsapp"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        :title="'Whatsapp Templates'"
        @click="$emit('selectWhatsappTemplate')"
      />
      <video-call-button
        v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
        :conversation-id="conversationId"
      />
      <AIAssistanceButton
        v-if="hasInboxPermission('Inbox ai assistance')"
        :conversation-id="conversationId"
        :is-private-note="isOnPrivateNote"
        :message="message"
        @replace-text="replaceText"
      />
      <woot-button
        v-if="closeReplyBox"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="closeReplyBox"
      >
        {{ $t('CONVERSATION.REPLYBOX.CANCEL') }}
      </woot-button>
      <transition name="modal-fade">
        <div
          v-show="$refs.upload && $refs.upload.dropActive"
          class="flex items-center justify-center gap-2 fixed left-0 right-0 top-0 bottom-0 w-full h-full z-20 text-slate-600 dark:text-slate-200 bg-white_transparent dark:bg-black_transparent flex-col"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="page-sub-title text-slate-600 dark:text-slate-200">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
    </div>
    <!-- d99d -->
    <div
      :dir="isRTLView ? 'rtl' : 'ltr'"
      :class="`right-wrap ${isRTLView ? 'rtl' : ''}`"
    >
      <div class="button-group">
        <woot-button
          v-if="showDelayButton"
          size="small"
          :class-names="buttonClass"
          :is-disabled="isSendDisabled"
          @click="toggleDelayModal"
        >
          {{ $t('CONVERSATION.REPLYBOX.SCHEDULE_SEND') }}
        </woot-button>
        <woot-button
          v-else
          size="small"
          :class-names="buttonClass"
          :is-disabled="isSendDisabled"
          @click="onSubmit"
        >
          {{ sendButtonText }}
        </woot-button>
        <woot-button
          v-if="!isOnPrivateNote && hasInboxPermission('Inbox delay message')"
          size="small"
          :icon="
            showDelayButton
              ? `chevron-${isRTLView ? 'right' : 'left'}`
              : 'ion-clock'
          "
          :is-disabled="isSendDisabled"
          :class-names="{ secondary: showDelayButton }"
          @click="toggleDelayButton"
        />
      </div>
    </div>
    <woot-modal :show.sync="showDelayModal" :on-close="hideDelayModal">
      <delay-message-modal
        :hours="delayOptions"
        :last-message-time="lastMessageTime"
        @set-delay="setDelay"
        @close="hideDelayModal"
      />
    </woot-modal>
    <!-- . -->
  </div>
</template>

<script>
import ExtendedFileUpload from './ExtendedFileUpload';
import * as ActiveStorage from 'activestorage';
import { hasPressedAltAndAKey } from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP,
} from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton.vue';
import AIAssistanceButton from '../AIAssistanceButton.vue';
import { REPLY_EDITOR_MODES } from './constants';
import { mixin as clickaway } from 'vue-clickaway';
import rtlMixin from 'shared/mixins/rtlMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';

const DocalDocumentsModal = () =>
  import('../conversation/DocalDocumentsModal.vue');

const FormsListModal = () => import('../conversation/FormsListModal.vue');

const BafiFileAttacher = () =>
  import(
    /* webpackChunkName: "platform_views" */ '../online99_platform/BafiFileAttacher'
  );

import { mapGetters } from 'vuex';
import { isFileExtAllowed } from '../../../helper/commons';
import DelayMessageModal from '../modal/DelayMessageModal.vue';

export default {
  name: 'ReplyBottomPanel',
  components: {
    BafiFileAttacher,
    FileUpload: ExtendedFileUpload,
    VideoCallButton,
    AIAssistanceButton,
    DelayMessageModal,
    DocalDocumentsModal,
    FormsListModal,
  },
  mixins: [
    eventListenerMixins,
    uiSettingsMixin,
    inboxMixin,
    clickaway,
    rtlMixin,
    hasPermissionMixin,
  ],
  props: {
    closeReplyBox: {
      type: Function,
      default: null,
    },
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '',
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    fileKindsAcceptedToUpload: {
      type: String,
      default: undefined,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    showEmojiPicker: {
      type: Boolean,
      default: false,
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    showEditorToggle: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    hasWhatsappTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    lastMessageTime: {
      type: String,
      default: null,
    },
  },
  // d99d
  data() {
    return {
      showDelayButton: false,
      showScheduleTimeDropdown: false,
      selectedDelayTime: null,
      delayOptions: Array.from({ length: 24 }, (_, i) => i + 1),
      showDelayModal: false,
      showDocalModal: false,
      showFormsModal: false,
    };
  },
  // .
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    isNote() {
      return this.mode === REPLY_EDITOR_MODES.NOTE;
    },
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    buttonClass() {
      return {
        warning: this.isNote,
      };
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      // Disable audio recorder for safari browser as recording is not supported
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
        navigator.userAgent
      );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) &&
        this.showAudioRecorder &&
        !isSafari
      );
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    allowedFileTypes() {
      if (this.isATwilioWhatsAppChannel) {
        return ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP;
      }
      return ALLOWED_FILE_TYPES;
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'microphone-pause';
        case 'paused':
          return 'microphone-play';
        case 'stopped':
          return 'microphone-play';
        default:
          return 'microphone-stop';
      }
    },
    showDocalButton() {
      return this.account?.additional_attributes?.document_signature_enabled;
    },
    showFormsButton() {
      return !!this.account?.forms_enabled;
    },
  },
  // d99d
  watch: {
    isOnPrivateNote() {
      this.showDelayButton = false;
      this.showScheduleTimeDropdown = false;
    },
  },
  // .
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    toggleDocal() {
      this.showDocalModal = !this.showDocalModal;
    },
    onDocalSigningLink(url) {
      this.showDocalModal = false;
      this.appendLinkToMessage(url);
    },
    toggleForms() {
      this.showFormsModal = !this.showFormsModal;
    },
    onFormLink(url) {
      this.showFormsModal = false;
      this.appendLinkToMessage(url);
    },
    appendLinkToMessage(url) {
      const currentMsg = this.message || '';
      const separator = currentMsg.trim() ? ' ' : '';
      this.$emit('replace-text', currentMsg + separator + url);
    },
    // d99d
    onSubmit() {
      this.onSend();
      if (this.closeReplyBox) this.closeReplyBox();
    },
    toggleDelayModal() {
      this.showDelayModal = !this.showDelayModal;
    },
    hideDelayModal() {
      this.showDelayModal = false;
    },
    setDelay(hours) {
      this.toggleDelayButton();
      this.$emit('set-delay', hours);
    },
    toggleDelayButton() {
      this.showDelayButton = !this.showDelayButton;
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (!isFileExtAllowed(newFile.name, this.fileKindsAcceptedToUpload)) {
          return prevent();
        }
      }
    },
    // .
    handleKeyEvents(e) {
      if (hasPressedAltAndAKey(e)) {
        this.$refs.upload.$children[1].$el.click();
      }
    },
    replaceText(text) {
      this.$emit('replace-text', text);
    },
  },
};
</script>

<style lang="scss" scoped>
.bottom-box {
  @apply flex justify-between py-3 px-4;

  &.is-note-mode {
    @apply bg-yellow-100 dark:bg-yellow-800;
  }
}

.left-wrap {
  @apply items-center flex gap-2;
}

.right-wrap {
  display: flex;

  .button-group {
    display: flex;
    flex-direction: row;
    .button {
      border-radius: 0; // Reset any border-radius
    }
  }
  &.rtl .button-group {
    .button:first-child {
      border-top-right-radius: var(--border-radius-normal);
      border-bottom-right-radius: var(--border-radius-normal);
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
    }

    .button:last-child {
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
      border-top-left-radius: var(--border-radius-normal);
      border-bottom-left-radius: var(--border-radius-normal);
      border-right: 2px solid white;
    }
  }

  &:not(.rtl) .button-group {
    .button:first-child {
      border-top-left-radius: var(--border-radius-normal);
      border-bottom-left-radius: var(--border-radius-normal);
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
      border-right: 2px solid white;
    }

    .button:last-child {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-top-right-radius: var(--border-radius-normal);
      border-bottom-right-radius: var(--border-radius-normal);
    }
  }
}

::v-deep .file-uploads {
  label {
    @apply cursor-pointer;
  }
  &:hover button {
    @apply dark:bg-slate-800 bg-slate-100;
  }
}
</style>
